/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'

import posthog from 'posthog-js'
import { useSearchParams } from 'react-router-dom'
import { useStopwatch } from 'react-timer-hook'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'

import { FaucetButton } from '@src/components/FaucetButton'
import MilesTracker from '@src/components/MilesTracker'
import TransactionStatusCard from '@src/components/TransactionStatusCard'
import ConnectButton from '@src/components/buttons/ConnectButton'
import {
    ChainId,
    NATIVE_TOKEN_ADDRESS,
    WRAPPED_GAS_TOKENS,
    getChainName,
    getChainNameFromChainId,
} from '@src/configs/chainInfo'
import { LARGE_SCREEN_THRESHOLD } from '@src/constants/exchange'
import { LiquiditySource, ResultCode, StepNames } from '@src/enums'
import { useFetchAllBalances } from '@src/hooks/useFetchAllBalances'
import { useFetchBoundReferralCode } from '@src/hooks/useFetchBoundReferralCode'
import { useFetchGeneratedReferralCode } from '@src/hooks/useFetchGeneratedReferralCode'
import { usePrecomputedGasPrices } from '@src/hooks/usePrecomputedGasPrices'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import AIInputBox from '@src/pages/Exchange/components/AIInputBox'
import { FromSection } from '@src/pages/Exchange/components/FromSection'
import { GasFeePanel } from '@src/pages/Exchange/components/GasFeePanel'
import RefreshButton from '@src/pages/Exchange/components/Refresh'
import { SwapActionButton } from '@src/pages/Exchange/components/SwapActionButton'
import SwapInProgressFlow from '@src/pages/Exchange/components/SwapInProgressFlow'
import { ToSection } from '@src/pages/Exchange/components/ToSection'
import { ToggleExchangeTokens } from '@src/pages/Exchange/components/ToggleExchangeTokens'
import { AllTooltips } from '@src/pages/Exchange/components/Tooltips'
import TransactionNotifications from '@src/pages/Exchange/components/TransactionNotifications'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import {
    useDestinationAddress,
    useExchangeAmountLogic,
    useExchangeDerivedState,
    useExchangeMemos,
    useExchangeToken,
    useGasEstimation,
    useOrderUpdates,
    useSwapContract,
    useToggleExchangeTokens,
} from '@src/pages/Exchange/hooks'
import { IExchangeData } from '@src/types/exchange'
import { FeatherQuoteError, IFeatherOrderCreateResponse, IFeatherOrderStatusResponse } from '@src/types/feather'
import { TransactionType } from '@src/types/gasPrices'
import { IQuoteData } from '@src/types/order'
import { Address, IToken } from '@src/types/token'
import {
    Balance,
    createContextLogger,
    formatExchangeAmountWithCommas,
    formatNumberStringWithCommas,
    getAllowance,
    isFeatureEnabled,
    isUnWrapOnly,
    isWrapOnly,
    returnSanitizedSellAmount,
} from '@src/utils'
import { mapBackendToFrontendStatus } from '@src/utils/featherUtils'
import {
    handleTokenSelection,
    isSameExchangeToken,
    validateDefaultTokens,
    validateUrlTokenParams,
} from '@src/utils/tokenValidation'
import { useApplicationStore, useConfigStore, useHistoryStore, useTokensStore } from '@src/zustand_stores'
import { useOrderUpdatesStore } from '@src/zustand_stores/orderUpdates.store'
import { useInvalidateBalances } from '@src/zustand_stores/tokens.store'

import FeatherFlow from './components/FeatherFlow'
import FeatherOrderStatusModal from './components/FeatherOrderFlow/FeatherOrderStatus'
import FeatherOrderNotice from './components/FeatherOrderNotice'
import useExchangeBalances from './hooks/useExchangeBalances'
import { DEFAULT_BITCOIN_ADDRESS, useFeatherOrder } from './hooks/useFeatherOrder'

const logger = createContextLogger('ExchangePage')

const ExchangePage = () => {
    const {
        isGasFeeExpanded,
        machFee,
        isReviewSwapModalOpen,
        isQuoteExpired,
        isManuallyRefreshing,
        isCustomRecipientWallet,
        hasInsufficientGas,
        isPriceFlipped,
        isToggleInProgress,
        sellAllowance,
        isQuoteEnabled,
        setIsGasFeeExpanded,
        setIsReviewSwapModalOpen,
        setIsCustomRecipientWallet,
        setIsPriceFlipped,
        setSellAllowance,
        setChainId,
        isBuyOutputLoading,
        setIsBuyOutputLoading,
    } = useExchangeState()

    const {
        isCrossChain,
        validateAddress,
        getAutoDetectedAddress,
        isCorrectWalletConnected,
        isWrapOrUnwrapOnly,
        isEVMConnected,
    } = useExchangeDerivedState()

    const {
        exchangeId,
        orderId,
        currentModalId,
        setExchangeId,
        setCurrentModalId,
        setWaitingForConnectedWalletInteraction,
        refreshStatus,
    } = useApplicationStore()

    const swapContracts = useConfigStore(config => config.swapContracts)
    const getOrder = useHistoryStore(state => state.getOrder)
    const updateHistory = useHistoryStore(state => state.updateHistory)
    const exchanges = useHistoryStore(state => state.history)
    const transactionData = getOrder(exchangeId)

    const [searchParams] = useSearchParams()

    const isFeatherEnabled = isFeatureEnabled('ENABLE_FEATHER')

    /**
     * * * * * * * * * * * * * * * * * * * * *  NEW ZUSTAND STORES * * * * * * * * * * * * * * *
     */
    const { isTronConnected, evmWalletAddress, tronWalletAddress, isSolanaConnected, solanaWalletAddress, EVMChainId } =
        useWalletConnections()

    const nativeTokenPricesInUSD = useTokensStore(state => state.nativeTokenPricesInUSD)
    const getGasTokenProps = useTokensStore(state => state.getGasTokenProps)

    /**
     * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
     */

    /**
     * * * * * * * * * * * * * * * * * * * * * NEW EXCHANGE DATA CONTEXT * * * * * * * * * * * * * * *
     */
    const {
        sellData,
        buyData,
        quoteResponse,
        isQuoteLoading,
        refetchQuote,
        invalidateQuoteAndRefetch,
        mainWalletAddress,
        isFeatherOrder,
        transactionCapError,
        isLoadingTransactionCap,
        liquidityAvailable,
        isCheckingLiquidity,
        liquidityError,
        updateSellData,
        updateBuyData,
        updateBothData,
    } = useExchangeDataContext()
    /**
     * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
     */

    useFetchAllBalances() // This will automatically update token balances in the store

    useFetchBoundReferralCode() // This will automatically update bound referral code in application store

    useFetchGeneratedReferralCode() // This will automatically update generated referral code in application store

    const { nativeBalance, wrappedNativeBalance, refreshAllBalancesAndWait } = useExchangeBalances()

    // Add the invalidateBalances function
    const invalidateBalances = useInvalidateBalances()

    useEffect(() => {
        if (isTronConnected) {
            setChainId(ChainId.Tron)
        } else if (isSolanaConnected) {
            setChainId(ChainId.Solana)
        } else if (EVMChainId) {
            setChainId(EVMChainId)
        }
    }, [isTronConnected, isSolanaConnected, EVMChainId])

    // Websocket hook which runs in the background and only connects if there is a valid exchangeId + orderId
    useOrderUpdates(exchangeId, orderId)

    const { precomputedGasPrices, isComputing, isGasPrecomputingDone } = usePrecomputedGasPrices(swapContracts)

    const {
        sellAmountString,
        isInsufficientBalance,
        isQuoteInvalid,
        liquiditySource,
        onChangeSellValue,
        handleManualRefresh,
        setMax,
        setSellAmountString,
    } = useExchangeAmountLogic({
        isComputing,
        isGasPrecomputingDone,
        precomputedGasPrices,
    })

    useEffect(() => {
        if (!isComputing && isGasPrecomputingDone) {
            // logger.info('[ExchangePage useEffect] precomputedGasPrices:', {
            //     precomputedGasPrices,
            // })
            if (precomputedGasPrices && nativeTokenPricesInUSD) {
                const computeUSDFees = () => {
                    const precomputedGasPricesInUSD: Record<number, Record<string, any>> = {}
                    for (const chainId of Object.keys(precomputedGasPrices)) {
                        precomputedGasPricesInUSD[+chainId] = {}

                        // Iterate over the values:
                        for (const txType of Object.values(TransactionType)) {
                            if (+chainId === ChainId.Solana) {
                                /// TODO
                            }

                            if (+chainId === ChainId.Tron) {
                                /// TODO
                            }

                            const txTypeResults = precomputedGasPrices[+chainId].results[txType as TransactionType]

                            if (txTypeResults.totalGasUnits && txTypeResults.totalFeePerGasUnit) {
                                const totalGasUnits = txTypeResults.totalGasUnits
                                const totalFeePerGasUnit = txTypeResults.totalFeePerGasUnit

                                const totalGas = totalGasUnits * totalFeePerGasUnit

                                const gasInToken = new Balance(getGasTokenProps(+chainId))
                                    .setValue(BigInt(totalGas))
                                    .toNumber()
                                const gasInUSD = gasInToken * nativeTokenPricesInUSD[+chainId]

                                precomputedGasPricesInUSD[+chainId][txType] = {
                                    gasInToken: gasInToken,
                                    gasInUSD: gasInUSD,
                                }
                            } else {
                                precomputedGasPricesInUSD[+chainId][txType] = {}
                            }
                        }
                    }

                    // logger.info('[ExchangePage useEffect] precomputedGasPricesInUSD:', {
                    //     precomputedGasPricesInUSD,
                    // })
                }

                computeUSDFees()
            }
        }
    }, [precomputedGasPrices, isComputing, isGasPrecomputingDone, swapContracts, nativeTokenPricesInUSD])

    const { isCCTP, hasErrors, computedMilesAndMultiplier, shortenedAddress, feesDisplay } = useExchangeMemos({
        sellData,
        buyData,
        isInsufficientBalance,
        isQuoteInvalid,
        isBuyOutputLoading,
        isQuoteExpired,
        address: evmWalletAddress,
        solanaWalletAddress: solanaWalletAddress?.toString(),
        machFee,
    })
    const miles = computedMilesAndMultiplier.miles

    const { reset: resetStopwatch } = useStopwatch({ autoStart: false })

    const onCloseReviewSwapModal = () => {
        const isLargeScreen = window.innerWidth >= LARGE_SCREEN_THRESHOLD

        // Show a Toast if needed...
        if (
            exchanges[currentModalId] &&
            exchanges[currentModalId].result?.status !== ResultCode.success &&
            [StepNames.MATCHING, StepNames.MATCH_CREATED, StepNames.MATCH_EXECUTED].includes(
                exchanges[currentModalId].currentStep
            ) &&
            exchanges[currentModalId].result?.status === ResultCode.pending
        ) {
            // Create a unique toast ID
            const safeToastId = `modal-close-${currentModalId}-${Date.now()}`

            // Use TransactionStatusCard with isModalCloseNotification=true to avoid toggle errors
            toast(
                <TransactionStatusCard
                    exchange={exchanges[currentModalId]}
                    onClose={() => toast.dismiss(safeToastId)}
                    isSuccess={false}
                    isModalCloseNotification={true}
                />,
                {
                    autoClose: 5000,
                    position: isLargeScreen ? 'bottom-right' : 'bottom-center',
                    toastId: safeToastId,
                    pauseOnHover: true,
                    pauseOnFocusLoss: false,
                    onClose: () => {},
                }
            )
        }

        // If swap completed successfully, track it and reset amounts
        if (exchanges[currentModalId]?.result?.status === ResultCode.success) {
            const exchange = exchanges[currentModalId]
            const sourceTxHash = exchange.result?.sourceTxHash || ''
            const destinationTxHash = exchange.result?.destinationTxHash || ''

            updateSellData({ value: BigInt(0) })
            updateBuyData({ value: BigInt(0) })
            setSellAmountString('') // Reset sell amount string
            resetStopwatch()

            // Invalidate balances to refresh after successful swap
            invalidateBalances()
        }

        // Reset states, close modal, etc.
        setWaitingForConnectedWalletInteraction(false)
        setIsReviewSwapModalOpen(false)
        refreshStatus()
    }

    useEffect(() => {
        // Re-sanitize sell string in case of token change, but only if we have a value
        if (sellData.value !== BigInt(0)) {
            setSellAmountString(
                formatNumberStringWithCommas(returnSanitizedSellAmount(sellAmountString, sellData.token.decimals)) ?? ''
            )
        }
    }, [sellData.token]) // Only run when token changes, not all sellData changes

    useEffect(() => {
        if (!sellData.token.native) {
            getAllowance(+sellData.chainId, sellData.token.address, mainWalletAddress || '', swapContract.address)
                .then(allowance => {
                    setSellAllowance(allowance)
                })
                .catch(e => {
                    logger.error(
                        `Cannot get allowance (chainId = ${+sellData.chainId}, token=${sellData.token.address},wallet=${mainWalletAddress || ''},swapContract=${swapContract.address})`,
                        e
                    )
                })
        }
    }, [sellData.token])

    const swapContract = useSwapContract(sellData, LiquiditySource.unavailable)

    const { onToggleExchangeTokens: onToggleExchangeTokensTokens } = useToggleExchangeTokens({
        isWrapOrUnwrapOnly: isWrapOnly(sellData, buyData) || isUnWrapOnly(sellData, buyData),
        isBuyOutputLoading,
        updateBothData,
        sellData,
        buyData,
        setSellAmountString,
    })

    // Remove the checkBalancesAndAllowances function and replace with this version
    const checkBalancesAndAllowances = async (
        tokenAddress: string,
        amount: bigint,
        isNative: boolean
    ): Promise<{ hasBalance: boolean; hasAllowance?: boolean }> => {
        try {
            let relevantBalance: bigint

            if (isNative) {
                relevantBalance = nativeBalance?.bigintValue || BigInt(0)
            } else if (tokenAddress === WRAPPED_GAS_TOKENS[sellData.chainId]?.address) {
                relevantBalance = wrappedNativeBalance?.bigintValue || BigInt(0)
            } else {
                relevantBalance = sellData.token.userBalance || BigInt(0)
            }

            if (relevantBalance < amount) {
                return { hasBalance: false }
            }

            // Only check allowance for non-native tokens
            if (!isNative) {
                const allowance = await getAllowance(
                    +sellData.chainId,
                    tokenAddress,
                    mainWalletAddress || '',
                    swapContract.address
                )

                return {
                    hasBalance: true,
                    hasAllowance: BigInt(allowance) >= amount,
                }
            }

            // Native tokens don't need allowance
            return {
                hasBalance: true,
                hasAllowance: true,
            }
        } catch (error) {
            logger.error('Error checking balances and allowances:', error)
            return {
                hasBalance: false,
                hasAllowance: false,
            }
        }
    }

    // Update onConfirmExchange to use the new function
    const onConfirmExchange = async () => {
        let initialStep = StepNames.UNSTARTED

        try {
            if (isWrapOnly(sellData, buyData)) {
                initialStep = StepNames.DEPOSIT_WETH
            } else if (isUnWrapOnly(sellData, buyData)) {
                initialStep = StepNames.UNWRAP_WETH
            } else {
                if (!sellData.token?.address || !sellData.value) {
                    initialStep = StepNames.UNSTARTED
                } else {
                    const isNativeToken = sellData.token.address === NATIVE_TOKEN_ADDRESS[sellData.chainId]
                    const wrappedToken = WRAPPED_GAS_TOKENS[sellData.chainId]

                    logger.info(
                        `[onConfirmExchange]: isNativeToken=${isNativeToken}, wrappedToken=${wrappedToken.address}, nativeBalance=`,
                        nativeBalance,
                        `wrappedNativeBalance=`,
                        wrappedNativeBalance
                    )

                    if (isNativeToken && wrappedToken) {
                        // Check native token balance first
                        const _nativeBalance = nativeBalance?.bigintValue || BigInt(0)

                        // TODO: Low priority: the max amount that can be spent in this case is ETH balance + WETH balance - gas fees in ETH.
                        // For example if I have 0.1 ETH and 0.3 WETH, I can spend 0.4 ETH (minus gas fees), but my 0.1 ETH
                        // would need to be wrapped first.
                        // So in case of native assets, we could only wrap the diff, since the solver spends WETH only
                        // If WETH balance > sellData.value, then the wrap step can be skipped.

                        // The maximum amount that can be spent is
                        if (_nativeBalance < sellData.value) {
                            initialStep = StepNames.UNSTARTED
                        } else {
                            // Check wrapped token balance and allowance
                            const { hasBalance, hasAllowance } = await checkBalancesAndAllowances(
                                wrappedToken.address,
                                sellData.value,
                                false
                            )

                            logger.info('[onConfirmExchange]: hasBalance=', hasBalance, 'hasAllowance=', hasAllowance)

                            if (!hasBalance) {
                                initialStep = StepNames.DEPOSIT_WETH
                            } else {
                                initialStep = hasAllowance ? StepNames.CONFIRM_SWAP : StepNames.APPROVE_TOKENS
                            }
                        }
                    } else {
                        // For non-native tokens
                        const { hasBalance, hasAllowance } = await checkBalancesAndAllowances(
                            sellData.token.address,
                            sellData.value,
                            false
                        )

                        logger.info('[onConfirmExchange]: hasBalance=', hasBalance, 'hasAllowance=', hasAllowance)

                        if (!hasBalance) {
                            initialStep = StepNames.UNSTARTED
                        } else {
                            initialStep = hasAllowance ? StepNames.CONFIRM_SWAP : StepNames.APPROVE_TOKENS
                        }
                    }
                }
            }
        } catch (error) {
            logger.error('Error determining initial step:', error)
            initialStep = StepNames.UNSTARTED
        }

        const newExchangeId = uuidv4()

        logger.info(`[onConfirmExchange] Determined initial step:`, initialStep)

        updateHistory(newExchangeId, {
            buyData,
            sellData,
            currentStep: initialStep,
        })

        setExchangeId(newExchangeId)
        setCurrentModalId(newExchangeId)

        setIsReviewSwapModalOpen(true)

        // Track when trade is confirmed by user
        posthog.capture('trade_confirmed', {
            from_token: sellData.token.symbol,
            to_token: buyData.token.symbol,
            from_amount: sellData.value.toString(),
            expected_to_amount: buyData.value.toString(),
            user_address: mainWalletAddress || evmWalletAddress || solanaWalletAddress?.toString() || tronWalletAddress,
            chain: sellData.chainId,
            fees: feesDisplay,
            miles: miles,
            initial_step: initialStep,
            is_cross_chain: isCrossChain,
            liquidity_source: liquiditySource,
        })
    }

    const referrer = useMemo(() => searchParams.get('referrer') ?? undefined, [searchParams.toString()])

    // Add handler for copying buy amount
    const handleCopyBuyAmount = () => {
        const amount = formatExchangeAmountWithCommas(buyData)
        navigator.clipboard.writeText(amount)
        toast.success('Amount copied to clipboard!', {
            position: 'bottom-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
        })
    }

    const exchangeTokenHandler = async (quote?: IQuoteData) => {
        try {
            await exchange(referrer, quote)
        } catch (err: any) {
            // Don't show error toast for user rejections
            if (err.message?.includes('User rejected') || err.message?.includes('user rejected') || err.code === 4001) {
                return
            }

            posthog.capture('onClickConfirmSwap_Fail', {
                wallet_address: evmWalletAddress,
            })

            // Show toast for other errors
            const errorMessage = (err.shortMessage || err.message || 'Error occurred').split('\n')[0]

            toast.error(errorMessage, {
                position: 'bottom-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            })
        }
    }

    const onClickConfirmSwap = () => {
        exchangeTokenHandler(quoteResponse.quote ?? undefined)

        // Calculate USD equivalents
        const sourceAmountUSD = sellData.token?.price_current
            ? (Number(sellData.value) * sellData.token.price_current) / 10 ** (sellData.token?.decimals || 18)
            : undefined

        const destinationAmountUSD = buyData.token?.price_current
            ? (Number(buyData.value) * buyData.token.price_current) / 10 ** (buyData.token?.decimals || 18)
            : undefined

        posthog.capture('onClickConfirmSwap', {
            wallet_address: evmWalletAddress,
            is_cctp: isCCTP,
            swap_type: isCCTP ? 'cctp' : 'market_maker',
            source_token: sellData.token?.symbol,
            source_chain: sellData.chainId,
            source_amount: sellData.value.toString(),
            source_amount_usd: sourceAmountUSD,
            source_token_price: sellData.token?.price_current,
            destination_token: buyData.token?.symbol,
            destination_chain: buyData.chainId,
            destination_amount: buyData.value.toString(),
            destination_amount_usd: destinationAmountUSD,
            destination_token_price: buyData.token?.price_current,
        })
    }

    const toggleCustomWallet = () => {
        logger.debug('[toggleCustomWallet] isCustomRecipientWallet:', { isCustomRecipientWallet })

        // If turning OFF custom wallet, reset to auto-detected address
        // If turning it ON, keep the current address (in case user is toggling back to a previously entered address)
        if (isCustomRecipientWallet) {
            // If we're turning it OFF
            const autoDetectedAddress = getAutoDetectedAddress(buyData.chainId)
            setDestinationAddress(autoDetectedAddress as Address)
        }

        // Toggle the custom wallet state
        setIsCustomRecipientWallet(prev => !prev)

        // Refetch quote after state update
        setTimeout(() => {
            refetchQuote()
        }, 0)
    }

    const { destinationAddress, setDestinationAddress } = useDestinationAddress({
        isCrossChain,
        buyData,
        sellData,
        evmWalletAddress,
        solanaWalletAddress,
        tronWalletAddress,
    })

    // Define a callback for successful swaps that refreshes balances
    const onSwapSuccess = async () => {
        // Refresh all balances and wait for them to update
        await refreshAllBalancesAndWait()
    }

    const { exchange } = useExchangeToken(exchangeId, liquiditySource, destinationAddress, onSwapSuccess)

    const onChangeDestAddress = (event: ChangeEvent<HTMLInputElement>) => {
        setDestinationAddress(event.target.value as Address)
    }

    useEffect(() => {
        // Only update destination address
        const detectedAddress = getAutoDetectedAddress(buyData.chainId)
        setDestinationAddress(detectedAddress as Address)
    }, [buyData.chainId, isEVMConnected, isSolanaConnected, isTronConnected])

    // Add this wrapper function inside your component
    const updateBothDataWrapper = (data: { sell: Partial<IExchangeData>; buy: Partial<IExchangeData> }) => {
        updateBothData(data.sell, data.buy)
    }

    // Then use the wrapper in your effect
    useEffect(() => {
        validateUrlTokenParams(searchParams, updateBothDataWrapper)
    }, []) // Run once on mount

    // Also add runtime validation whenever tokens change
    useEffect(() => {
        // Skip if tokens aren't loaded yet
        if (!sellData.token || !buyData.token) return

        // Check for same token on same chain
        if (isSameExchangeToken(sellData, buyData)) {
            logger.info('Detected same token on both sides - applying validated defaults')

            // Get validated default tokens that won't create duplicates
            const { sellToken, buyToken } = validateDefaultTokens(sellData, buyData)

            // If only the sell token needs to be reset
            if (buyToken === buyData.token) {
                updateSellData({
                    token: sellToken,
                    chainId: sellToken.chainId,
                })
            }
            // If both tokens need to be reset
            else {
                updateBothData(
                    {
                        token: sellToken,
                        chainId: sellToken.chainId,
                    },
                    {
                        token: buyToken,
                        chainId: buyToken.chainId,
                    }
                )
            }
        }
    }, [sellData.token?.address, buyData.token?.address, sellData.chainId, buyData.chainId])

    // Update your token selection handler to use the helper
    const onTokenSelectionChange = (newData: { token?: IToken; chainId?: number }, type: 'buy' | 'sell') => {
        handleTokenSelection(newData, type, sellData, buyData, updateBothDataWrapper)

        // Refetch quote after token selection to ensure we have an updated quote
        // Use setTimeout to ensure state updates have completed
        setTimeout(() => {
            refetchQuote()
        }, 0)
    }

    // Update the cleanup effect
    useEffect(() => {
        const abortController = new AbortController()

        return () => {
            abortController.abort()
        }
    }, [])

    // Refetch quote when token selection changes
    useEffect(() => {
        // Skip if we're in the middle of a swap operation
        if (isToggleInProgress) {
            return
        }

        // Skip for wrap/unwrap only
        if (isWrapOrUnwrapOnly) {
            return
        }

        if (sellData.token?.address && buyData.token?.address && sellData.value !== BigInt(0)) {
            // Track when user starts the trade flow
            posthog.capture('trade_initiated', {
                from_token: sellData.token.symbol,
                to_token: buyData.token.symbol,
                from_amount: sellData.value.toString(),
                expected_to_amount: buyData.value.toString(),
                user_address:
                    mainWalletAddress || evmWalletAddress || solanaWalletAddress?.toString() || tronWalletAddress,
                chain: sellData.chainId,
            })

            setTimeout(() => {
                refetchQuote()
            }, 0)
        }
    }, [
        `${sellData.token?.address}-${sellData.token?.chainId}-${buyData.token?.address}-${buyData.token?.chainId}-${sellData.value}`,
        isToggleInProgress, // Add isToggleInProgress to dependencies
    ])

    // Replace the manual gas calculation with the hook
    const gasFeeString = useGasEstimation({
        sellData,
        sellAllowance,
        precomputedGasPrices,
        nativeTokenPricesInUSD,
        isCCTP,
        isWrapOrUnwrapOnly,
    })

    // Feather order state
    const [isFeatherOrderModalOpen, setIsFeatherOrderModalOpen] = useState(false)
    const [isFeatherStatusModalOpen, setIsFeatherStatusModalOpen] = useState(false)

    // Track modal state changes in the OrderUpdates store for background notifications
    const setModalOpen = useOrderUpdatesStore(state => state.setModalOpen)

    useEffect(() => {
        if (exchangeId && orderId) {
            const isModalOpen = isReviewSwapModalOpen || isFeatherStatusModalOpen
            console.log(
                `[bg-tx] 🔄 Setting modal state for ${exchangeId}:${orderId} to ${isModalOpen ? 'OPEN' : 'CLOSED'}`
            )
            setModalOpen(exchangeId, orderId, isModalOpen)
        }
    }, [exchangeId, orderId, isReviewSwapModalOpen, isFeatherStatusModalOpen, setModalOpen])
    const [featherOrderData, setFeatherOrderData] = useState<IFeatherOrderCreateResponse | null>(null)
    const [featherOrderStatus, setFeatherOrderStatus] = useState<IFeatherOrderStatusResponse | null>(null)

    // Get the chain name for the Feather notice
    const buyChainName = useMemo(() => {
        const chainNameKey = getChainNameFromChainId(buyData.chainId)
        return getChainName(chainNameKey)
    }, [buyData.chainId])

    const { featherQuote, getQuote, featherQuoteError } = useFeatherOrder({
        onOrderCreated: data => {
            setFeatherOrderData(data)
        },
        onOrderStatusChanged: () => {},
    })

    // State for Feather quote error
    const [featherQuoteErrorState, setFeatherQuoteErrorState] = useState<FeatherQuoteError | null>(null)

    // Automatically enable Custom Wallet mode when Bitcoin is selected as buy chain
    // and set default Bitcoin address if none is set yet
    useEffect(() => {
        if (!isFeatherEnabled) {
            return
        }

        if (buyData.chainId === ChainId.Bitcoin) {
            // Enable custom wallet
            setIsCustomRecipientWallet(true)

            if (!destinationAddress) {
                logger.info('LMAO: Setting default Bitcoin address on chain change', {
                    address: DEFAULT_BITCOIN_ADDRESS,
                })
                setDestinationAddress(DEFAULT_BITCOIN_ADDRESS as Address)
            }
        }
    }, [buyData.chainId, setIsCustomRecipientWallet, destinationAddress, setDestinationAddress])

    // Effect to fetch Feather quotes for BTC destination
    useEffect(() => {
        // Skip if feature is not enabled or not a Feather chain or if we already have a valid quote
        if (
            !isFeatherEnabled ||
            !isFeatherOrder ||
            isFeatherOrderModalOpen ||
            sellData.featherQuoteId ||
            !destinationAddress ||
            sellData.value === BigInt(0) ||
            buyData.value > BigInt(0)
        ) {
            setIsBuyOutputLoading(false)
            return
        }

        // Don't continue if we've already encountered an error on the current input values
        if (featherQuoteErrorState) {
            setIsBuyOutputLoading(false)
            return
        }

        // Track if an API call has been attempted
        let hasAttemptedFetch = false

        // Use a debounce timer to avoid too many API calls
        let debounceTimer: NodeJS.Timeout | null = null

        // Set loading state before starting the debounce
        setIsBuyOutputLoading(true)

        const fetchFeatherQuote = async () => {
            // Don't make multiple fetch attempts if we've already tried
            if (hasAttemptedFetch) {
                return
            }

            hasAttemptedFetch = true

            try {
                // Get a quote
                const quoteData = await getQuote()

                // If we got a quote error, store it and stop further fetching
                if (quoteData && 'error' in quoteData) {
                    logger.error('Error fetching Feather quote:', quoteData.error)
                    setFeatherQuoteErrorState({
                        message: quoteData.error,
                        type: quoteData.errorType || 'unknown',
                    })
                    return
                }

                // If we got a quote, update sellData with Feather-specific info
                if (quoteData) {
                    // Create updated sellData with Feather quote info
                    const updatedSellData = {
                        ...sellData,
                        isFeatherOrder: true,
                        featherQuoteId: quoteData.quoteId,
                        featherExpiresAt: quoteData.expiresAt,
                    }

                    // Update the sell data
                    updateSellData(updatedSellData)
                }
            } catch (error) {
                // Error is handled in the getQuote function
                logger.error('Error in fetchFeatherQuote:', error)
            } finally {
                // Always clear loading state when finished, whether successful or not
                setIsBuyOutputLoading(false)
            }
        }

        // Clear any existing timer
        if (debounceTimer) {
            clearTimeout(debounceTimer)
        }

        // Set a new timer with a shorter debounce (1000ms)
        debounceTimer = setTimeout(fetchFeatherQuote, 1000)

        // Cleanup function
        return () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer)
            }
            // Clear loading state when effect is cleaned up
            setIsBuyOutputLoading(false)
        }
    }, [
        isFeatherOrder,
        sellData.value,
        buyData.value,
        destinationAddress,
        getQuote,
        isFeatherOrderModalOpen,
        sellData.featherQuoteId,
        sellData.featherExpiresAt,
        updateSellData,
        setIsBuyOutputLoading,
        featherQuoteErrorState,
    ])

    // Update Feather quote error state when the error from the hook changes
    useEffect(() => {
        if (featherQuoteError !== featherQuoteErrorState?.message) {
            if (featherQuoteError) {
                setFeatherQuoteErrorState({
                    message: featherQuoteError,
                    type: 'unknown',
                })
            } else {
                setFeatherQuoteErrorState(null)
            }
        }
    }, [featherQuoteError])

    // Clear Feather quote error when inputs change
    useEffect(() => {
        // Only clear error state when inputs actually change in a meaningful way
        if (
            sellData.value > BigInt(0) && // Only if we have a non-zero value
            featherQuoteErrorState !== null // And we actually have an error to clear
        ) {
            logger.debug('Clearing Feather quote error because inputs changed', {
                sellValue: sellData.value.toString(),
                sellToken: sellData.token?.symbol,
                buyToken: buyData.token?.symbol,
                destinationAddress,
            })
            setFeatherQuoteErrorState(null)
        }
    }, [sellData.value, sellData.token, buyData.token, destinationAddress])

    // Handler for creating a Feather order
    const onCreateFeatherOrder = async () => {
        if (!isFeatherEnabled) {
            logger.warn('Feather orders are disabled')
            return
        }

        // Add debugging
        console.log('onCreateFeatherOrder called', {
            isFeatherOrder: sellData.isFeatherOrder,
            featherQuoteId: sellData.featherQuoteId,
            destinationAddress,
            buyChainId: buyData.chainId,
        })

        // For Bitcoin orders specifically, use default address if none is provided
        if (!destinationAddress && buyData.chainId === ChainId.Bitcoin) {
            logger.info('LMAO: Using default Bitcoin address for order creation', {
                address: DEFAULT_BITCOIN_ADDRESS,
            })
            setDestinationAddress(DEFAULT_BITCOIN_ADDRESS as Address)
            // Give the state time to update before continuing
            await new Promise(resolve => setTimeout(resolve, 100))
        } else if (!destinationAddress) {
            toast.error('Please enter a destination address')
            return
        }

        // Use the existing Feather quote/data
        if (!sellData.isFeatherOrder || !sellData.featherQuoteId) {
            logger.error('Cannot proceed: no feather quote data available')
            toast.error('No valid quote data found')
            return
        }

        // Simply open the modal with the existing quote data
        setIsFeatherOrderModalOpen(true)

        // Additional debugging after state update
        console.log('Setting isFeatherOrderModalOpen to true', {
            isFeatherOrderModalOpen: true,
            hasFeatherQuote: !!featherQuote,
            depositAddress: featherQuote?.srcDepositWallet,
        })
    }

    // Add a debug effect to monitor loading states
    useEffect(() => {
        logger.debug('Loading state debug:', {
            isBuyOutputLoading,
            isQuoteLoading,
            isFeatherOrder,
            hasSellValue: sellData.value > BigInt(0),
            hasDestinationAddress: !!destinationAddress,
            sellAmount: sellData.value.toString(),
            buyAmount: buyData.value.toString(),
            hasFeatherQuoteId: !!sellData.featherQuoteId,
        })
    }, [
        isBuyOutputLoading,
        isQuoteLoading,
        isFeatherOrder,
        sellData.value,
        destinationAddress,
        buyData.value,
        sellData.featherQuoteId,
    ])

    // Function to manually retry fetching a Feather quote after an error
    const retryFeatherQuote = useCallback(() => {
        // Skip if feature is disabled
        if (!isFeatherEnabled) {
            return
        }

        // Only retry if there's currently an error
        if (featherQuoteErrorState) {
            logger.info('Manually retrying Feather quote', {
                sellAmount: sellData.value.toString(),
                destinationAddress,
                errorType: featherQuoteErrorState.type,
            })

            // Clear the error state
            setFeatherQuoteErrorState(null)

            // Trigger a refetch immediately
            setTimeout(async () => {
                try {
                    setIsBuyOutputLoading(true)
                    const quoteData = await getQuote()

                    // Handle error if any
                    if (quoteData && 'error' in quoteData) {
                        logger.error('Error in retry attempt for Feather quote:', quoteData.error)
                        setFeatherQuoteErrorState({
                            message: quoteData.error,
                            type: quoteData.errorType || 'unknown',
                        })
                        return
                    }

                    // If we got a quote, update sellData with Feather-specific info
                    if (quoteData) {
                        const updatedSellData = {
                            ...sellData,
                            isFeatherOrder: true,
                            featherQuoteId: quoteData.quoteId,
                            featherExpiresAt: quoteData.expiresAt,
                        }

                        // Update the sell data
                        updateSellData(updatedSellData)
                    }
                } catch (error) {
                    logger.error('Error in retry for Feather quote:', error)
                } finally {
                    setIsBuyOutputLoading(false)
                }
            }, 0)
        }
    }, [featherQuoteErrorState, sellData, destinationAddress, getQuote, updateSellData, setIsBuyOutputLoading])

    // Modify handleManualRefresh to clear Feather errors and force a new quote fetch
    const handleManualRefreshWrapper = () => {
        // Clear any Feather quote errors when manually refreshing
        if (isFeatherOrder && featherQuoteErrorState) {
            logger.info('Manual refresh: clearing Feather quote error state')
            setFeatherQuoteErrorState(null)

            // Force refresh by setting loading and then triggering the quote fetch
            setIsBuyOutputLoading(true)
            setTimeout(async () => {
                try {
                    const quoteData = await getQuote()

                    // Handle error if any
                    if (quoteData && 'error' in quoteData) {
                        logger.error('Error in manual refresh for Feather quote:', quoteData.error)
                        setFeatherQuoteErrorState({
                            message: quoteData.error,
                            type: quoteData.errorType || 'unknown',
                        })
                        return
                    }

                    // If we got a quote, update sellData with Feather-specific info
                    if (quoteData) {
                        const updatedSellData = {
                            ...sellData,
                            isFeatherOrder: true,
                            featherQuoteId: quoteData.quoteId,
                            featherExpiresAt: quoteData.expiresAt,
                        }

                        // Update the sell data
                        updateSellData(updatedSellData)
                    }
                } catch (error) {
                    logger.error('Error in manual refresh for Feather quote:', error)
                } finally {
                    setIsBuyOutputLoading(false)
                }
            }, 0)

            return
        }

        // Call the original refresh handler for non-Feather trades
        handleManualRefresh()
    }

    // Effect to monitor Feather order modal state and debug if needed
    useEffect(() => {
        if (isFeatherEnabled && isFeatherOrderModalOpen) {
            logger.info('Feather order modal is open with data:', {
                isFeatherOrder: sellData.isFeatherOrder,
                featherQuoteId: sellData.featherQuoteId,
                featherExpiresAt: sellData.featherExpiresAt,
                buyChain: buyData.chainId,
                depositAddress: featherQuote?.srcDepositWallet,
            })
        }
    }, [
        isFeatherOrderModalOpen,
        sellData.isFeatherOrder,
        sellData.featherQuoteId,
        sellData.featherExpiresAt,
        buyData.chainId,
        featherQuote,
    ])

    // Effect to monitor feather quotes and state
    useEffect(() => {
        if (isFeatherEnabled && isFeatherOrder && sellData.value > BigInt(0)) {
            logger.debug('Feather order status update:', {
                isFeatherOrder,
                hasFeatherQuote: !!featherQuote,
                hasSellDataQuoteId: !!sellData.featherQuoteId,
                featherQuoteExpiry: sellData.featherExpiresAt,
                depositAddress: featherQuote?.srcDepositWallet,
                buyChainId: buyData.chainId,
                isModalOpen: isFeatherOrderModalOpen,
            })
        }
    }, [
        isFeatherOrder,
        sellData.featherQuoteId,
        sellData.featherExpiresAt,
        featherQuote,
        sellData.value,
        buyData.chainId,
        isFeatherOrderModalOpen,
    ])

    return (
        <>
            <AIInputBox className='mb-2' setMax={setMax} />
            <div className='w-[464px] max-w-[calc(100%-24px)] space-y-2 rounded-2xl border border-mach-modal-border bg-mach-modal-background px-2 py-4'>
                <div className='flex items-center justify-between px-4'>
                    <span className='text-[18px] font-medium'>Swap</span>
                    <RefreshButton
                        disabled={
                            !isQuoteEnabled || !sellData.value || Number(sellData.value) === 0 || isWrapOrUnwrapOnly
                        }
                        isLoading={
                            (liquidityAvailable &&
                                isQuoteEnabled &&
                                (isBuyOutputLoading || isManuallyRefreshing || isQuoteLoading)) ||
                            isCheckingLiquidity
                        }
                        onClick={handleManualRefreshWrapper}
                    />
                </div>

                {/* Display Feather Order Notice below the Swap title */}
                {isFeatherEnabled && isFeatherOrder && (
                    <div className='mb-2 px-4'>
                        <FeatherOrderNotice chainName={buyChainName} />
                    </div>
                )}

                <div className='space-y-2'>
                    <div className='space-y-[6px]'>
                        <FromSection
                            sellData={sellData}
                            buyData={buyData}
                            isCorrectWalletConnected={isCorrectWalletConnected}
                            isInsufficientBalance={isInsufficientBalance}
                            isComputingGas={isComputing}
                            quoteResponse={quoteResponse}
                            isGasPrecomputingDone={isGasPrecomputingDone}
                            sellAmountString={sellAmountString}
                            onChangeSellValue={onChangeSellValue}
                            onSetMax={setMax}
                            onTokenSelectionChange={onTokenSelectionChange}
                        />

                        <ToggleExchangeTokens onToggleExchangeTokens={onToggleExchangeTokensTokens} />

                        <ToSection
                            buyData={buyData}
                            sellData={sellData}
                            isCustomRecipientWallet={isCustomRecipientWallet}
                            isCrossChain={isCrossChain}
                            onToggleCustomWallet={toggleCustomWallet}
                            buyAmountString={formatExchangeAmountWithCommas(buyData)}
                            isBuyOutputLoading={isBuyOutputLoading}
                            isQuoteLoading={isQuoteLoading}
                            isCheckingLiquidity={isCheckingLiquidity}
                            liquidityAvailable={liquidityAvailable}
                            onTokenSelectionChange={onTokenSelectionChange}
                            onClickAmount={handleCopyBuyAmount}
                            destinationAddress={destinationAddress}
                            validateAddress={validateAddress}
                            onChangeDestAddress={onChangeDestAddress}
                        />
                    </div>

                    {buyData.value !== BigInt(0) && (
                        <GasFeePanel
                            isGasFeeExpanded={isGasFeeExpanded}
                            gasFeeString={gasFeeString}
                            onToggleGasFee={() => setIsGasFeeExpanded(!isGasFeeExpanded)}
                            detailedRowsProps={{
                                destinationAddress,
                                evmWalletAddress,
                                isCustomRecipientWallet,
                                shortenedAddress,
                                sellData,
                                buyData,
                                gasFeeString,
                                precomputedGasPrices,
                                liquiditySource,
                                machFee,
                                miles,
                            }}
                            isPriceFlipped={isPriceFlipped}
                            setIsPriceFlipped={setIsPriceFlipped}
                            sellData={sellData}
                            buyData={buyData}
                            isWrapOrUnwrapOnly={isWrapOrUnwrapOnly}
                            isBuyOutputLoading={isBuyOutputLoading}
                            isQuoteLoading={isQuoteLoading}
                        />
                    )}

                    {/* Exchange page swap button logic. */}
                    {(() => {
                        let elements: JSX.Element[] = []

                        if (!isCorrectWalletConnected) {
                            elements = [
                                <div className='space-y-2' key='connect-button'>
                                    <ConnectButton className='h-14' requiredChainType={sellData.chainId} />
                                </div>,
                            ]
                        } else {
                            elements.push(
                                <SwapActionButton
                                    key={`feather-swap-${sellData.value.toString()}-${buyData.value.toString()}-${sellData.featherQuoteId || ''}`}
                                    sellData={sellData}
                                    buyData={buyData}
                                    sellAmountString={sellAmountString}
                                    quoteResponse={quoteResponse}
                                    errors={{
                                        isInsufficientBalance,
                                        isQuoteInvalid,
                                    }}
                                    isBuyOutputLoading={isBuyOutputLoading}
                                    hasInsufficientGas={hasInsufficientGas}
                                    isCustomRecipientWallet={
                                        isCustomRecipientWallet || buyData.chainId === ChainId.Bitcoin
                                    }
                                    destinationAddress={destinationAddress}
                                    onConfirmExchange={onConfirmExchange}
                                    onCreateFeatherOrder={onCreateFeatherOrder}
                                    onRetryFeatherQuote={retryFeatherQuote}
                                    gasFeeSimulationInProgress={isComputing}
                                    isQuoteLoading={isQuoteLoading}
                                    featherQuoteError={featherQuoteErrorState}
                                    isLiquidityUnavailable={!liquidityAvailable}
                                    isCheckingLiquidity={isCheckingLiquidity}
                                    liquidityError={liquidityError}
                                />
                            )

                            // Add Faucet buttons if we're in testing mode
                            // if (isFeatureEnabled('USE_TESTNETS_ONLY')) {
                            //     // Check if we need Monad faucet
                            //     if (
                            //         sellData.chainId === ChainId.MonadTestnet ||
                            //         buyData.chainId === ChainId.MonadTestnet
                            //     ) {
                            //         elements.push(
                            //             <FaucetButton
                            //                 key='monad-faucet'
                            //                 walletAddress={mainWalletAddress}
                            //                 chainId={ChainId.MonadTestnet}
                            //                 className='mt-2 w-full'
                            //                 initialCollapsed={true}
                            //             />
                            //         )
                            //     }

                            //     // Check if we need Sepolia faucet
                            //     if (sellData.chainId === ChainId.Sepolia || buyData.chainId === ChainId.Sepolia) {
                            //         elements.push(
                            //             <FaucetButton
                            //                 key='sepolia-faucet'
                            //                 walletAddress={mainWalletAddress}
                            //                 chainId={ChainId.Sepolia}
                            //                 className='mt-2 w-full'
                            //                 initialCollapsed={true}
                            //             />
                            //         )
                            //     }

                            //     // Check if we need MegaETH faucet
                            //     if (
                            //         sellData.chainId === ChainId.MegaEthTestnet ||
                            //         buyData.chainId === ChainId.MegaEthTestnet
                            //     ) {
                            //         elements.push(
                            //             <FaucetButton
                            //                 key='megaeth-faucet'
                            //                 walletAddress={mainWalletAddress}
                            //                 chainId={ChainId.MegaEthTestnet}
                            //                 className='mt-2 w-full'
                            //                 initialCollapsed={true}
                            //             />
                            //         )
                            //     }
                            // }

                            if (miles > 0 && !hasErrors && !isFeatureEnabled('USE_TESTNETS_ONLY')) {
                                elements.push(<MilesTracker key='miles' miles={miles} />)
                            }
                        }

                        return elements
                    })()}
                </div>
            </div>

            {/* Feather Order Flow Modal */}
            {isFeatherEnabled && sellData.isFeatherOrder && sellData.featherQuoteId && (
                <FeatherFlow
                    isOpen={isFeatherOrderModalOpen}
                    onClose={() => {
                        console.log('Closing Feather order modal')
                        setIsFeatherOrderModalOpen(false)
                    }}
                    sellData={sellData}
                    buyData={buyData}
                    fees={[
                        {
                            label: 'Network Fee',
                            value: 0,
                            display: '$0.00',
                        },
                        {
                            label: 'Liquidity Provider Fee',
                            value: 0,
                            display: '$0.00',
                        },
                    ]}
                    depositAddress={(featherQuote?.srcDepositWallet as Address) || ('' as Address)}
                    miles={miles}
                />
            )}

            {/* Feather Order Status Modal */}
            {isFeatherEnabled && featherOrderStatus && (
                <FeatherOrderStatusModal
                    isOpen={isFeatherStatusModalOpen}
                    onClose={() => setIsFeatherStatusModalOpen(false)}
                    sellData={sellData}
                    buyData={buyData}
                    depositTxHash={featherOrderStatus.depositTransaction?.txHash}
                    destinationTxHash={featherOrderStatus.destinationTransaction?.txHash}
                    status={mapBackendToFrontendStatus(featherOrderStatus.status)}
                    orderId={featherOrderStatus.orderId}
                />
            )}

            {transactionData && currentModalId && (!isFeatherOrder || !isFeatherEnabled) && (
                <SwapInProgressFlow
                    isOpen={isReviewSwapModalOpen}
                    onClose={onCloseReviewSwapModal}
                    onConfirm={onClickConfirmSwap}
                    fees={feesDisplay}
                    miles={miles}
                    destinationAddress={destinationAddress || mainWalletAddress}
                    liquiditySource={liquiditySource}
                    {...transactionData}
                />
            )}

            {/* Modal state tracking through TransactionNotifications component */}
            <TransactionNotifications />

            <AllTooltips />
        </>
    )
}

export default ExchangePage
